import React, { useState } from 'react';
import { Upload, Button, Icon, Alert, Collapse, notification, Spin } from 'antd';
import FileSaver from 'file-saver';
import * as HttpStatus from 'http-status-codes';
import { MSG_ERROR } from '../../constants';
import { getApiErrorMessage } from '../../utils/httpError';
import { closeLoadingNotification, openLoadingNotification } from '../../utils/loadingNotification';
import { getExpandedErrorPanelKeys, parseImportErrorPayload } from './importErrors';
import './Import.scss';

const { Dragger } = Upload;
const { Panel } = Collapse;
const FEEDBACK_SUCCESS = 'success';
const FEEDBACK_ERROR = 'error';
const FEEDBACK_WARNING = 'warning';

const CREATED_LIST_KEYS = [
  'createdUsers',
  'createdContacts',
  'createdCustomers',
  'createdSuppliers',
  'createdItems',
  'imported',
  'importedRecords',
  'successes',
  'successful',
];

const FAILED_LIST_KEYS = [
  'excludedUsers',
  'excludedContacts',
  'excluded',
  'failures',
  'failedRecords',
];

function pickNumericField(source, ...keys) {
  if (!source || typeof source !== 'object') return null;
  for (let i = 0; i < keys.length; i += 1) {
    const value = source[keys[i]];
    if (value != null && value !== '') {
      const parsed = Number(value);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return null;
}

function firstArrayField(source, keys) {
  if (!source || typeof source !== 'object') return null;
  for (let i = 0; i < keys.length; i += 1) {
    const value = source[keys[i]];
    if (Array.isArray(value)) return value;
  }
  return null;
}

function mapImportFailures(items = []) {
  return items
    .map((item) => {
      if (typeof item === 'string') {
        return { rowNumber: null, email: '', reason: item.trim() };
      }
      return {
        rowNumber: pickNumericField(item, 'rowNumber', 'row', 'RowNumber', 'Row'),
        email: String(item?.email ?? item?.Email ?? '').trim(),
        reason: String(item?.reason ?? item?.message ?? item?.error ?? item?.ErrorMessage ?? '').trim(),
      };
    })
    .filter((item) => item.rowNumber != null || item.email || item.reason);
}

function tryParseJson(value) {
  if (typeof value !== 'string') return value;
  const trimmed = value.trim();
  if (!trimmed || (trimmed[0] !== '[' && trimmed[0] !== '{')) return value;
  try {
    return JSON.parse(trimmed);
  } catch (err) {
    return value;
  }
}

function unwrapImportPayload(payload) {
  const parsed = tryParseJson(payload);
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return parsed;
  if (parsed.data == null || typeof parsed.data !== 'object') return parsed;

  const nested = tryParseJson(parsed.data);
  const hasOwnCounts =
    pickNumericField(parsed, 'successCount', 'failedCount', 'total', 'importedCount', 'createdCount') != null ||
    firstArrayField(parsed, CREATED_LIST_KEYS) != null ||
    firstArrayField(parsed, FAILED_LIST_KEYS) != null;

  return hasOwnCounts ? parsed : nested;
}

function isRecordArray(value) {
  return Array.isArray(value) && value.length > 0 && value.every((item) => item && typeof item === 'object');
}

/**
 * Best-effort parser for contact/customer/supplier-style import responses
 * when a caller-specific parseImportResult is not provided.
 * Supports a bare array of created records, e.g. contact import:
 * [{ contactId, firstName, lastName, email, ... }, ...]
 */
function defaultParseImportResult(payload) {
  const data = unwrapImportPayload(payload);
  if (data == null) return null;

  if (Array.isArray(data)) {
    if (data.length === 0) return null;
    return { total: data.length, successCount: data.length, failedCount: 0, failures: [] };
  }

  if (typeof data !== 'object') return null;

  if (
    typeof data.successCount === 'number' &&
    typeof data.failedCount === 'number' &&
    Array.isArray(data.failures)
  ) {
    return {
      total: data.total ?? data.successCount + data.failedCount,
      successCount: data.successCount,
      failedCount: data.failedCount,
      failures: data.failures,
    };
  }

  // Some APIs wrap the created records, e.g. { contacts: [...] } or { data: [...] }.
  const nestedRecords =
    firstArrayField(data, [
      'contacts',
      'customers',
      'suppliers',
      'records',
      'items',
      'results',
      ...CREATED_LIST_KEYS,
    ]) ?? (isRecordArray(data.data) ? data.data : null);

  if (isRecordArray(nestedRecords) && firstArrayField(data, FAILED_LIST_KEYS) == null) {
    const onlyRecordList =
      pickNumericField(data, 'successCount', 'failedCount', 'total', 'importedCount', 'createdCount') == null;
    if (onlyRecordList || nestedRecords === data.contacts || nestedRecords === data.records) {
      return {
        total: nestedRecords.length,
        successCount: nestedRecords.length,
        failedCount: 0,
        failures: [],
      };
    }
  }

  const created = firstArrayField(data, CREATED_LIST_KEYS) ?? [];
  const excluded = firstArrayField(data, FAILED_LIST_KEYS) ?? [];
  const hasLists = firstArrayField(data, CREATED_LIST_KEYS) != null || firstArrayField(data, FAILED_LIST_KEYS) != null;

  const successCount =
    pickNumericField(data, 'successCount', 'successcount', 'importedCount', 'imported', 'createdCount', 'created') ??
    (hasLists ? created.length : null);
  const failedCount =
    pickNumericField(data, 'failedCount', 'failedcount', 'failed') ?? (hasLists ? excluded.length : null);

  if (successCount == null && failedCount == null) return null;

  const success = successCount ?? 0;
  const failed = failedCount ?? 0;

  return {
    total: pickNumericField(data, 'total', 'totalRows', 'totalCount') ?? success + failed,
    successCount: success,
    failedCount: failed,
    failures: mapImportFailures(excluded),
  };
}

function DuplicateErrorsTable({ duplicateGroups }) {
  return (
    <>
      <p className="import-errors__summary">
        These rows match records that already exist or appear more than once in your file. Remove or update them, then
        try importing again.
      </p>
      <div className="import-errors__table-wrap">
        <table className="import-errors__table">
          <thead>
            <tr>
              <th>Row</th>
              <th>Record</th>
              <th>Identifier</th>
            </tr>
          </thead>
          <tbody>
            {duplicateGroups.map(group =>
              group.records.map((record, index) => (
                <tr key={`${group.row ?? 'unknown'}-${record.name}-${record.identifier}`}>
                  {index === 0 && (
                    <td className="import-errors__row-label" rowSpan={group.records.length}>
                      {group.row != null ? `Row ${group.row}` : '—'}
                    </td>
                  )}
                  <td className="import-errors__name">{record.name}</td>
                  <td className="import-errors__identifier">{record.identifier || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ImportResultSummary({ result }) {
  if (!result) return null;

  const { total, successCount, failedCount, failures, isUncountedSuccess } = result;
  const allSucceeded = (successCount > 0 && failedCount === 0) || isUncountedSuccess;

  if (allSucceeded) {
    return (
      <div className="import-summary import-summary--brief">
        <p className="import-summary__brief">
          {successCount > 0 ? (
            <>
              All <strong>{successCount}</strong> record{successCount === 1 ? '' : 's'} uploaded successfully.
            </>
          ) : (
            <>All records uploaded successfully.</>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="import-summary">
      <ul className="import-summary__stats">
        <li>
          Total records: <strong>{total}</strong>
        </li>
        <li>
          Successfully uploaded: <strong>{successCount}</strong>
        </li>
        <li>
          Failed: <strong>{failedCount}</strong>
        </li>
      </ul>
      {failures.length > 0 ? (
        <>
          <p className="import-summary__heading">Failed records:</p>
          <ul className="import-summary__failures">
            {failures.map((failure) => {
              const rowLabel = failure.rowNumber != null ? `Row ${failure.rowNumber}` : 'Row —';
              const emailPart = failure.email ? ` – ${failure.email}` : '';
              const reasonPart = failure.reason ? `: ${failure.reason}` : '';
              const key = [failure.rowNumber ?? 'row', failure.email, failure.reason].join('|');
              return (
                <li key={key}>
                  {rowLabel}
                  {emailPart}
                  {reasonPart}
                </li>
              );
            })}
          </ul>
        </>
      ) : null}
    </div>
  );
}

function feedbackFromImportResult(result) {
  if (!result) return { status: '', message: '' };

  if (result.isUncountedSuccess) {
    return { status: FEEDBACK_SUCCESS, message: 'Imported successfully' };
  }

  if (result.successCount > 0 && result.failedCount === 0) {
    return {
      status: FEEDBACK_SUCCESS,
      message: `Successfully uploaded ${result.successCount} record${result.successCount === 1 ? '' : 's'}.`,
    };
  }

  if (result.successCount > 0) {
    return {
      status: FEEDBACK_WARNING,
      message: `Upload completed. ${result.successCount} of ${result.total} record${
        result.total === 1 ? '' : 's'
      } uploaded successfully.`,
    };
  }

  return {
    status: FEEDBACK_ERROR,
    message: 'No records were uploaded. Review the failed records below.',
  };
}

function ValidationErrorsTable({ validationGroups }) {
  return (
    <>
      <p className="import-errors__summary">
        These rows have missing or invalid data. Fix them in your spreadsheet, then upload the file again.
      </p>
      <div className="import-errors__table-wrap">
        <table className="import-errors__table">
          <thead>
            <tr>
              <th>Row</th>
              <th>Issue</th>
            </tr>
          </thead>
          <tbody>
            {validationGroups.map(group =>
              group.messages.map((message, index) => (
                <tr key={`${group.row ?? 'unknown'}-${message}`}>
                  {index === 0 && (
                    <td className="import-errors__row-label" rowSpan={group.messages.length}>
                      {group.row != null ? `Row ${group.row}` : '—'}
                    </td>
                  )}
                  <td className="import-errors__issue">{message}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}

export default ({
  warning,
  templateName = 'template.xlsx',
  downloadTemplate,
  importFromTemplate,
  uploadHint,
  parseImportResult,
  onImportComplete,
  persistedImportResult = null,
}) => {
  const [uploading, setUploading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState('');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [importSummary, setImportSummary] = useState(null);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [validationGroups, setValidationGroups] = useState([]);
  const [expandedErrorPanels, setExpandedErrorPanels] = useState([]);

  const clearImportFeedback = () => {
    setImportSummary(null);
    setDuplicateGroups([]);
    setValidationGroups([]);
    setExpandedErrorPanels([]);
  };

  const onDownloadTemplate = async () => {
    if (downloading) {
      return;
    }
    setDownloading(true);
    openLoadingNotification({ key: 'downloading', message: 'Downloading' });
    try {
      const res = await downloadTemplate();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      FileSaver.saveAs(blob, templateName);
      closeLoadingNotification('downloading');
      notification.success({
        key: 'download_success',
        message: 'Download successfully'
      });
    } catch (err) {
      closeLoadingNotification('downloading');
      notification.error({
        key: 'download_fail',
        message: 'Download failed',
        description: getApiErrorMessage(err, MSG_ERROR)
      });
    }
    setDownloading(false);
  };

  const setErrorMessage = err => {
    const payload = err?.data ?? err?.response?.data ?? err;
    const { duplicateGroups: nextDuplicateGroups, validationGroups: nextValidationGroups } = parseImportErrorPayload(payload);

    setFeedbackStatus(FEEDBACK_ERROR);

    if (nextDuplicateGroups.length || nextValidationGroups.length) {
      setDuplicateGroups(nextDuplicateGroups);
      setValidationGroups(nextValidationGroups);
      setExpandedErrorPanels(getExpandedErrorPanelKeys(nextDuplicateGroups, nextValidationGroups));
      setFeedbackMessage('Import could not be completed. Review the issues below and update your file.');
      return;
    }

    setFeedbackMessage(typeof payload === 'string' ? payload : getApiErrorMessage({ data: payload }, MSG_ERROR));
    clearImportFeedback();
  };

  const onImport = async ({ file, onSuccess, onError }) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      clearImportFeedback();
      const response = await importFromTemplate(formData);
      const payload = response?.data ?? response;
      const result = parseImportResult?.(payload) ?? defaultParseImportResult(payload);

      if (result) {
        setImportSummary(result);

        if (result.successCount > 0 && result.failedCount === 0) {
          setFeedbackStatus(FEEDBACK_SUCCESS);
          setFeedbackMessage(`Successfully uploaded ${result.successCount} record${result.successCount === 1 ? '' : 's'}.`);
        } else if (result.successCount > 0) {
          setFeedbackStatus(FEEDBACK_WARNING);
          setFeedbackMessage(
            `Upload completed. ${result.successCount} of ${result.total} record${result.total === 1 ? '' : 's'} uploaded successfully.`,
          );
        } else if (result.failedCount > 0) {
          setFeedbackStatus(FEEDBACK_ERROR);
          setFeedbackMessage('No records were uploaded. Review the failed records below.');
        } else {
          setFeedbackStatus(FEEDBACK_SUCCESS);
          setFeedbackMessage('Imported successfully');
        }
      } else {
        const uncountedSuccess = {
          total: 0,
          successCount: 0,
          failedCount: 0,
          failures: [],
          isUncountedSuccess: true,
        };
        setImportSummary(uncountedSuccess);
        setFeedbackStatus(FEEDBACK_SUCCESS);
        setFeedbackMessage('Imported successfully');
      }

      if (onImportComplete) {
        await onImportComplete(payload, result);
      }

      onSuccess(null, file);
      setUploading(false);
    } catch (err) {
      onError();
      const status = err?.response?.status ?? err?.status;
      const data = err?.response?.data ?? err?.data;
      switch (status) {
        case HttpStatus.BAD_REQUEST:
          setErrorMessage({ data, status });
          setUploading(false);
          break;
        default:
          setFeedbackStatus(FEEDBACK_ERROR);
          setFeedbackMessage(getApiErrorMessage(err, MSG_ERROR));
          clearImportFeedback();
          setUploading(false);
          break;
      }
    }
  };

  const hasDuplicateErrors = duplicateGroups.length > 0;
  const hasValidationErrors = validationGroups.length > 0;
  const duplicateCount = duplicateGroups.reduce((total, group) => total + group.records.length, 0);
  const validationIssueCount = validationGroups.reduce((total, group) => total + group.messages.length, 0);
  const displayedSummary = importSummary || persistedImportResult;
  const isBriefAllSuccess =
    displayedSummary &&
    ((displayedSummary.successCount > 0 && displayedSummary.failedCount === 0) ||
      displayedSummary.isUncountedSuccess);
  const persistedFeedback = feedbackFromImportResult(displayedSummary);
  const alertStatus = feedbackMessage ? feedbackStatus : persistedFeedback.status;
  const alertMessage = feedbackMessage || persistedFeedback.message;
  // All-success uses the brief summary panel only — avoid a second success alert.
  const showFeedbackAlert = Boolean(alertMessage) && !isBriefAllSuccess;

  return (
    <Spin spinning={uploading} wrapperClassName="import-panel-spin">
      <div className="import-panel">
        <Dragger customRequest={onImport} showUploadList={false}>
          <p className="ant-upload-drag-icon">
            <Icon type="inbox" />
          </p>
          <p className="ant-upload-text">Click or drag file to this area to upload</p>
          <p className="ant-upload-hint">
            {uploadHint ||
              'Please download the template and insert records you want to import, then upload the template file'}
          </p>
        </Dragger>
        {warning && (
          <Alert type="warning" showIcon message="Warning" description={warning} style={{ marginTop: '10px' }} />
        )}
        <div className="btn-container no-padding">
          <Button type="primary" icon="download" loading={downloading} onClick={onDownloadTemplate}>
            Download Template
          </Button>
        </div>
        {showFeedbackAlert ? (
          <Alert message={alertMessage} type={alertStatus || FEEDBACK_SUCCESS} style={{ marginTop: '10px' }} />
        ) : null}
        {displayedSummary ? <ImportResultSummary result={displayedSummary} /> : null}
        {(hasDuplicateErrors || hasValidationErrors) && (
          <Collapse
            bordered={false}
            className="import-errors"
            activeKey={expandedErrorPanels}
            onChange={keys => setExpandedErrorPanels(Array.isArray(keys) ? keys : keys ? [keys] : [])}
          >
            {hasDuplicateErrors && (
              <Panel key="duplicates" header={`Duplicate or existing records (${duplicateCount})`}>
                <DuplicateErrorsTable duplicateGroups={duplicateGroups} />
              </Panel>
            )}
            {hasValidationErrors && (
              <Panel
                key="validation"
                header={`Validation errors (${validationIssueCount} ${validationIssueCount === 1 ? 'issue' : 'issues'})`}
              >
                <ValidationErrorsTable validationGroups={validationGroups} />
              </Panel>
            )}
          </Collapse>
        )}
      </div>
    </Spin>
  );
};
